import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import MetaTags from "react-meta-tags"
import { withRouter } from "react-router-dom"
import { withTranslation } from "react-i18next"
import { connect } from "react-redux"
import {
  Card,
  Tabs,
  Row,
  Col,
  Space,
  Typography,
  notification,
  Breadcrumb,
  Badge,
  Alert,
} from "antd"
import {
  WalletOutlined,
  DollarOutlined,
  FileTextOutlined,
  BarChartOutlined,
  HomeOutlined,
} from "@ant-design/icons"
import EngPaymentDashboard from "../Dashboard/components/EngPaymentDashboard"
import InvoiceEngList from "../Accounting/engInvoices/invoice-list"
import EngSummaryFinance from "../Accounting/engSummary"
import { resetAccountingFlag } from "store/actions"

const { Title, Text } = Typography

notification.config({
  duration: 3,
  rtl: true,
  placement: "topLeft",
})

const EngAccountingDashboard = props => {
  const { t, error, success, onResetAccountingFlag } = props
  const [activeTab, setActiveTab] = useState("1")
  const [api, contextHolder] = notification.useNotification()

  // Show notifications
  const openNotification = (type, message) => {
    api[type]({
      message: type === "success" ? "موفقیت" : type === "error" ? "خطا" : "هشدار",
      description: message,
    })
  }

  useEffect(() => {
    if (success) openNotification("success", success)
    if (error) openNotification("error", error)
    if (success || error) onResetAccountingFlag()
  }, [success, error])

  // Get tab from URL query parameter
  useEffect(() => {
    const query = new URLSearchParams(props.location.search)
    const tabIndex = query.get("tab") || "1"
    setActiveTab(tabIndex)
  }, [props.location.search])

  const handleTabChange = (key) => {
    setActiveTab(key)
    // Update URL with tab parameter
    props.history.push(`${props.location.pathname}?tab=${key}`)
  }

  const tabItems = [
    {
      key: "1",
      label: (
        <Space>
          <DollarOutlined />
          <span>پرداخت‌های من</span>
          <Badge count={0} showZero={false} />
        </Space>
      ),
      children: <EngPaymentDashboard />,
    },
    {
      key: "2",
      label: (
        <Space>
          <FileTextOutlined />
          <span>فاکتورها</span>
        </Space>
      ),
      children: <InvoiceEngList translate={t} />,
    },
    {
      key: "3",
      label: (
        <Space>
          <BarChartOutlined />
          <span>خلاصه کارکرد</span>
        </Space>
      ),
      children: <EngSummaryFinance userInfo={props.userInfo} />,
    },
  ]

  const breadcrumbItems = [
    {
      title: (
        <Space>
          <HomeOutlined />
          <span>خانه</span>
        </Space>
      ),
      href: "/dashboard",
    },
    {
      title: (
        <Space>
          <WalletOutlined />
          <span>حسابداری من</span>
        </Space>
      ),
    },
    {
      title: tabItems.find(item => item.key === activeTab)?.label || "پرداخت‌های من",
    },
  ]

  return (
    <div className="page-content" style={{ background: '#f5f7fa', minHeight: '100vh', padding: '24px' }}>
      <MetaTags>
        <title>حسابداری من | سامانه واحد گاز</title>
      </MetaTags>
      {contextHolder}

      {/* Header Card */}
      <Card
        style={{
          marginBottom: 24,
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          border: 'none',
        }}
        bodyStyle={{ padding: '24px 32px' }}
      >
        <Row justify="space-between" align="middle">
          <Col>
            <Space direction="vertical" size={4}>
              <Breadcrumb items={breadcrumbItems} style={{ color: '#fff' }} />
              <Title level={2} style={{ margin: 0, color: '#fff', fontWeight: 700 }}>
                <Space>
                  <WalletOutlined style={{ fontSize: 32 }} />
                  حسابداری من
                </Space>
              </Title>
              <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14 }}>
                مدیریت پرداخت‌ها، فاکتورها و گزارشات مالی
              </Text>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Alerts */}
      {error && (
        <Alert
          message="خطا"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
      )}
      {success && (
        <Alert
          message="موفقیت"
          description={success}
          type="success"
          showIcon
          closable
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
      )}

      {/* Main Content Card */}
      <Card
        style={{
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          minHeight: 'calc(100vh - 280px)',
        }}
        bodyStyle={{ padding: 0 }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabItems}
          size="large"
          tabBarStyle={{
            padding: '0 24px',
            margin: 0,
            background: '#fafafa',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
          }}
          style={{
            height: '100%',
          }}
        />
      </Card>

      {/* Custom Styles */}
      <style>{`
        .page-content .ant-tabs-tab {
          padding: 12px 20px;
          font-size: 15px;
          font-weight: 500;
          transition: all 0.3s ease;
        }
        
        .page-content .ant-tabs-tab:hover {
          color: #667eea;
        }
        
        .page-content .ant-tabs-tab.ant-tabs-tab-active {
          background: #fff;
          border-radius: 8px 8px 0 0;
        }
        
        .page-content .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
          color: #667eea;
          font-weight: 600;
        }
        
        .page-content .ant-tabs-ink-bar {
          background: #667eea;
          height: 3px;
          border-radius: 3px;
        }
        
        .page-content .ant-tabs-content-holder {
          padding: 24px;
          background: #fff;
          border-radius: 0 0 12px 12px;
        }
        
        .page-content .ant-breadcrumb a {
          color: rgba(255,255,255,0.85);
          transition: color 0.3s ease;
        }
        
        .page-content .ant-breadcrumb a:hover {
          color: #fff;
        }
        
        .page-content .ant-breadcrumb-separator {
          color: rgba(255,255,255,0.65);
        }
        
        @media (max-width: 768px) {
          .page-content {
            padding: 16px;
          }
          
          .page-content .ant-tabs-content-holder {
            padding: 16px;
          }
        }
      `}</style>
    </div>
  )
}

EngAccountingDashboard.propTypes = {
  t: PropTypes.func,
  location: PropTypes.object,
  history: PropTypes.object,
  userInfo: PropTypes.object,
  error: PropTypes.string,
  success: PropTypes.string,
  onResetAccountingFlag: PropTypes.func,
}

const mapStateToProps = ({ USERs, Accounting }) => ({
  userInfo: USERs.userInfo,
  error: Accounting.error,
  success: Accounting.success,
})

const mapDispatchToProps = dispatch => ({
  onResetAccountingFlag: () => dispatch(resetAccountingFlag()),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(withTranslation()(EngAccountingDashboard)))
